import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./StoreDetail.module.scss";
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Tag,
  Typography,
  Space,
  Skeleton,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  DollarOutlined,
  ShoppingOutlined,
  FlagOutlined,
  LineChartOutlined,
  TrophyOutlined,
  TeamOutlined,
  WarningOutlined,
  AlertOutlined,
  ThunderboltOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as CTitle,
  Legend,
  Tooltip as CTooltip,
  Filler,
} from "chart.js";
import {
  getStoreAnalysisDetailBreakdown,
  getStoreAnalysisDetailChart,
  getStoreAnalysisDetailCore,
} from "../../../api/Services";
import type {
  StoreAnalysisDetailBreakdown,
  StoreAnalysisDetailChart,
  StoreAnalysisDetailCore,
} from "../../../types/storeAnalysis";
import { useFetchRequestGuard } from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";
import StoreRankList from "./StoreRankList";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  CTitle,
  Legend,
  CTooltip,
  Filler
);

const { Title, Text } = Typography;
const fmt = (v: number) =>
  `$ ${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const ALERT_I18N_KEYS: Record<string, string> = {
  SALES_DROP_VS_LY: "StoreAnalysis-Alert-SalesDropVsLY",
  AVG_TICKET_DECLINE: "StoreAnalysis-Alert-AvgTicketDecline",
  MARGIN_DECLINE: "StoreAnalysis-Alert-MarginDecline",
  OVERSTOCK: "StoreAnalysis-Alert-Overstock",
  PROMO_DEPENDENCY: "StoreAnalysis-Alert-PromoDependency",
  TOP3_CATEGORY_CONCENTRATION: "StoreAnalysis-Alert-Top3CategoryConcentration",
  LOW_TICKET_ACTIVITY: "StoreAnalysis-Alert-LowTicketActivity",
  SUSTAINED_DECLINE: "StoreAnalysis-Alert-SustainedDecline",
};

const SEVERITY_COLORS: Record<string, string> = {
  ALTA: "red",
  MEDIA: "orange",
  BAJA: "gold",
};

const CAUTION_INSIGHT_KEYS = new Set([
  "StoreAnalysis-Insight-SalesConcentration",
  "StoreAnalysis-Insight-PromoDependency",
  "StoreAnalysis-Insight-DeclineCategories",
  "StoreAnalysis-Insight-PromoDependencyIncrease",
]);

const Vr: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const StoreDetailHeaderSkeleton: React.FC = () => (
  <Card className={styles.headerCard}>
    <Skeleton.Input active style={{ width: 280, height: 28 }} />
  </Card>
);

const StoreDetailKpisSkeleton: React.FC = () => (
  <div className={styles.kpiRow}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Card className={styles.kpiCard} key={i}>
        <Skeleton active paragraph={{ rows: 2, width: ["60%", "80%"] }} title={false} />
      </Card>
    ))}
  </div>
);

const StoreDetailChartSkeleton: React.FC = () => (
  <Card className={styles.sectionCard}>
    <Skeleton.Input active size="small" style={{ width: 220, height: 16, marginBottom: 16 }} />
    <Skeleton.Input active style={{ width: "100%", height: 220 }} />
  </Card>
);

const StoreDetailBreakdownSkeleton: React.FC = () => (
  <>
    <Row gutter={[16, 16]} className={styles.topRow}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Col xs={24} lg={8} key={i}>
          <Card className={styles.sectionCard}>
            <Skeleton active paragraph={{ rows: 5 }} />
          </Card>
        </Col>
      ))}
    </Row>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 10 }} />
    </Card>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 6 }} />
    </Card>
    <Card className={`${styles.sectionCard} ${styles.clientsSectionCard}`}>
      <Skeleton active paragraph={{ rows: 12 }} />
    </Card>
  </>
);

const StoreDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(30);
  const [core, setCore] = useState<StoreAnalysisDetailCore | null>(null);
  const [chart, setChart] = useState<StoreAnalysisDetailChart | null>(null);
  const [breakdown, setBreakdown] =
    useState<StoreAnalysisDetailBreakdown | null>(null);
  const [coreLoading, setCoreLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const storeId = Number(id);

  const fetchDetail = useCallback(() => {
    if (!storeId) return;
    const requestId = beginRequest();
    setCoreLoading(true);
    setChartLoading(true);
    setBreakdownLoading(true);

    const params = { period };

    // Fired together and resolved independently — none of them depends on another's
    // result, so each section paints as soon as its own request comes back.
    getStoreAnalysisDetailCore(storeId, params).then((result) => {
      if (isStaleRequest(requestId)) return;
      setCore(result);
      setCoreLoading(false);
    });

    getStoreAnalysisDetailChart(storeId, params).then((result) => {
      if (isStaleRequest(requestId)) return;
      setChart(result);
      setChartLoading(false);
    });

    getStoreAnalysisDetailBreakdown(storeId, params).then((result) => {
      if (isStaleRequest(requestId)) return;
      setBreakdown(result);
      setBreakdownLoading(false);
    });
  }, [storeId, period, beginRequest, isStaleRequest]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const chartData = useMemo(() => {
    const points = chart?.salesEvolution || [];
    return {
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: t("StoreAnalysis-Historical", "Histórico"),
          data: points.map((p) => p.sales),
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139, 92, 246, 0.08)",
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 2,
        },
      ],
    };
  }, [chart?.salesEvolution, t]);

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { usePointStyle: true, font: { size: 12 } },
      },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.04)" } },
      x: { grid: { display: false } },
    },
  };

  if (!coreLoading && !core) {
    return (
      <div className={styles.wrapper}>
        <Button
          icon={<ArrowLeftOutlined />}
          type="text"
          className={styles.backBtn}
          onClick={() => navigate("/commercial-analysis/stores")}
        >
          {t("StoreAnalysis-Stores", "Tiendas")}
        </Button>
        <Empty description={t("StoreAnalysis-NotFound", "Tienda no encontrada")} />
      </div>
    );
  }

  const mix = breakdown?.mix || [];
  const quiebres = breakdown?.quiebres || [];
  const clientKpis = breakdown?.clientKpis;
  const clientesNuevosMes = breakdown?.clientesNuevosMes || [];
  const ubicacionGeografica = breakdown?.ubicacionGeografica || [];

  const mixCols = [
    { title: t("StoreAnalysis-Brand", "MARCA"), dataIndex: "marca", key: "marca", width: 100 },
    {
      title: t("StoreAnalysis-Category", "CATEGORÍA"),
      dataIndex: "categoria",
      key: "categoria",
      width: 160,
    },
    {
      title: t("StoreAnalysis-Article", "ARTÍCULO"),
      key: "articulo",
      render: (_: unknown, r: (typeof mix)[0]) => (
        <div>
          <Text strong>{r.articulo}</Text>
          {r.externalId && (
            <div>
              <Text type="secondary" className={styles.externalId}>
                {r.externalId}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("StoreAnalysis-GrossSales", "VENTA BRUTA"),
      key: "ventaBruta",
      align: "right" as const,
      render: (_: unknown, r: (typeof mix)[0]) => (
        <div className={styles.cellStack}>
          <Text strong>{fmt(r.ventaBruta)}</Text>
          <Vr value={r.ventaVar} />
        </div>
      ),
    },
    {
      title: t("StoreAnalysis-Units", "UNIDADES"),
      key: "unidades",
      align: "right" as const,
      render: (_: unknown, r: (typeof mix)[0]) => (
        <div className={styles.cellStack}>
          <Text strong>{fmtN(r.unidades)}</Text>
          <Vr value={r.unidadesVar} />
        </div>
      ),
    },
    {
      title: t("StoreAnalysis-Tickets", "TICKETS"),
      key: "tickets",
      align: "right" as const,
      render: (_: unknown, r: (typeof mix)[0]) => (
        <div className={styles.cellStack}>
          <Text strong>{fmtN(r.tickets)}</Text>
          <Vr value={r.ticketsVar} />
        </div>
      ),
    },
    {
      title: t("StoreAnalysis-AvgTicket", "TICKET PROMEDIO"),
      key: "ticketProm",
      align: "right" as const,
      render: (_: unknown, r: (typeof mix)[0]) => (
        <div className={styles.cellStack}>
          <Text strong>{fmt(r.ticketPromedio)}</Text>
          <Vr value={r.ticketPromVar} />
        </div>
      ),
    },
    {
      title: t("StoreAnalysis-Margin", "MARGEN"),
      key: "margen",
      align: "right" as const,
      render: (_: unknown, r: (typeof mix)[0]) => (
        <div className={styles.cellStack}>
          <Text strong>{r.margenPct.toFixed(1)}%</Text>
          <Vr value={r.margenVar} suffix=" pp" />
        </div>
      ),
    },
    {
      title: t("StoreAnalysis-StoreSalesPct", "% VENTA TIENDA"),
      dataIndex: "pctVentaTienda",
      key: "pctVentaTienda",
      render: (v: number) => `${v.toFixed(1)}%`,
      align: "right" as const,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Button
        icon={<ArrowLeftOutlined />}
        type="text"
        className={styles.backBtn}
        onClick={() => navigate("/commercial-analysis/stores")}
      >
        {t("StoreAnalysis-Stores", "Tiendas")}
      </Button>

      {coreLoading ? (
        <StoreDetailHeaderSkeleton />
      ) : (
        core && (
          <Card className={styles.headerCard}>
            <div className={styles.headerRow}>
              <Title level={3} className={styles.pageTitle}>
                {core.nombre}
              </Title>
              <div className={styles.headerRight}>
                <Select
                  value={period}
                  onChange={setPeriod}
                  className={styles.filterSelect}
                  options={[
                    { value: 30, label: t("StoreAnalysis-Last30Days", "Últimos 30 días") },
                    { value: 60, label: t("StoreAnalysis-Last60Days", "Últimos 60 días") },
                    { value: 90, label: t("StoreAnalysis-Last90Days", "Últimos 90 días") },
                    { value: 180, label: t("StoreAnalysis-Last180Days", "Últimos 180 días") },
                  ]}
                />
              </div>
            </div>
          </Card>
        )
      )}

      {coreLoading ? (
        <StoreDetailKpisSkeleton />
      ) : (
        core && (
          <div className={styles.kpiRow}>
            {[
              {
                label: t("StoreAnalysis-GrossSales", "VENTA BRUTA"),
                value: fmt(core.kpis.ventaBruta),
                variation: core.kpis.ventaVar,
                icon: <DollarOutlined />,
              },
              {
                label: t("StoreAnalysis-Units", "UNIDADES"),
                value: fmtN(core.kpis.unidades),
                variation: core.kpis.unidadesVar,
                icon: <TagOutlined />,
              },
              {
                label: t("StoreAnalysis-AvgTicket", "TICKET PROMEDIO"),
                value: fmt(core.kpis.ticketPromedio),
                variation: core.kpis.ticketPromVar,
                icon: <ShoppingCartOutlined />,
                compare: `${t("StoreAnalysis-Global", "Global")}: ${fmt(core.kpis.ticketPromedioGlobal)}`,
              },
              {
                label: t("StoreAnalysis-BasketSize", "TAMAÑO CESTA"),
                value: core.kpis.tamanoCesta.toFixed(2),
                variation: core.kpis.tamanoCestaVar,
                icon: <ShoppingOutlined />,
              },
              {
                label: t("StoreAnalysis-Margin", "MARGEN"),
                value: `${core.kpis.margenPct.toFixed(1)}%`,
                variation: core.kpis.margenVar,
                icon: <FlagOutlined />,
                suf: " pp",
              },
            ].map((item, i) => (
              <Card className={styles.kpiCard} key={i}>
                <div className={styles.kpiIcon}>{item.icon}</div>
                <Text className={styles.kpiLabel}>{item.label}</Text>
                <div className={styles.kpiValue}>{item.value}</div>
                <Vr value={item.variation} suffix={item.suf || "%"} />
                {item.compare && (
                  <Text type="secondary" className={styles.kpiCompare}>
                    {item.compare}
                  </Text>
                )}
              </Card>
            ))}
          </div>
        )
      )}

      {chartLoading ? (
        <StoreDetailChartSkeleton />
      ) : (
        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <LineChartOutlined />
              <span>
                {t(
                  "StoreAnalysis-SalesEvolution",
                  "Evolución de ventas y forecast"
                )}
              </span>
            </Space>
          }
        >
          <div className={styles.chartContainer}>
            {(chart?.salesEvolution || []).length > 0 ? (
              <Line data={chartData} options={chartOpts} />
            ) : (
              <Empty
                description={t("StoreAnalysis-NoSales", "Sin ventas en el período")}
              />
            )}
          </div>
        </Card>
      )}

      {breakdownLoading || !breakdown ? (
        <StoreDetailBreakdownSkeleton />
      ) : (
        <>
        <Row gutter={[16, 16]} className={styles.topRow}>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <AlertOutlined style={{ color: "#f5222d" }} />
                  <span>{t("StoreAnalysis-ActiveAlerts", "Alertas activas")}</span>
                </Space>
              }
            >
              <div className={styles.bulletList}>
                {(breakdown.alerts || []).length === 0 ? (
                  <Text type="secondary">
                    {t("StoreAnalysis-NoAlertsDetected", "Sin alertas activas en este período.")}
                  </Text>
                ) : (
                  breakdown.alerts.map((a, i) => (
                    <div className={styles.bulletItem} key={i}>
                      <AlertOutlined className={styles.bulletRed} />
                      <Text>{t(ALERT_I18N_KEYS[a.key] ?? a.key)}</Text>
                      {a.severity && (
                        <Tag color={SEVERITY_COLORS[a.severity] ?? "default"}>
                          {a.severity.charAt(0) + a.severity.slice(1).toLowerCase()}
                        </Tag>
                      )}
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <ThunderboltOutlined style={{ color: "#1890ff" }} />
                  <span>{t("StoreAnalysis-DetectedDrivers", "Drivers detectados")}</span>
                </Space>
              }
            >
              <div className={styles.bulletList}>
                {(breakdown.drivers || []).length === 0 ? (
                  <Text type="secondary">
                    {t("StoreAnalysis-NoDriversDetected", "Sin drivers detectados en este período.")}
                  </Text>
                ) : (
                  breakdown.drivers.map((d, i) => (
                    <div className={styles.bulletItem} key={i}>
                      <ThunderboltOutlined className={styles.bulletBlue} />
                      <Text>{t(d.key, d.params)}</Text>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <BulbOutlined style={{ color: "#8b5cf6" }} />
                  <span>{t("StoreAnalysis-Insights", "Insights")}</span>
                </Space>
              }
            >
              <div className={styles.insightList}>
                {(breakdown.insights || []).length === 0 ? (
                  <Text type="secondary">
                    {t("StoreAnalysis-NoInsightsDetected", "Sin insights detectados en este período.")}
                  </Text>
                ) : (
                  breakdown.insights.map((insight, i) => (
                    <div
                      key={i}
                      className={`${styles.insightItem} ${
                        CAUTION_INSIGHT_KEYS.has(insight.key)
                          ? styles.insightYellow
                          : styles.insightGreen
                      }`}
                    >
                      <Text>{t(insight.key, insight.params)}</Text>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className={styles.topRow}>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <TrophyOutlined className={styles.topIconPurple} />
                  <span>{t("StoreAnalysis-TopBrands", "Top Marcas")}</span>
                </Space>
              }
            >
              <StoreRankList items={breakdown.topBrands} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <TrophyOutlined className={styles.topIconBlue} />
                  <span>{t("StoreAnalysis-TopCategories", "Top Categorías")}</span>
                </Space>
              }
            >
              <StoreRankList items={breakdown.topCategories} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <TrophyOutlined className={styles.topIconGreen} />
                  <span>{t("StoreAnalysis-TopArticles", "Top Artículos")}</span>
                </Space>
              }
            >
              <StoreRankList items={breakdown.topArticles} />
            </Card>
          </Col>
        </Row>

        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <ShoppingCartOutlined />
              <span>{t("StoreAnalysis-SalesMix", "Mix de ventas")}</span>
            </Space>
          }
        >
          <Table
            columns={mixCols}
            dataSource={mix.map((row) => ({ ...row, key: row.articleId }))}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            scroll={{ x: 1200 }}
            className={styles.dataTable}
          />
        </Card>

        <Card
          className={styles.sectionCard}
          title={
            <Space>
              <WarningOutlined className={styles.warningIcon} />
              <span>{t("StoreAnalysis-Stockouts", "Quiebres de stock")}</span>
            </Space>
          }
        >
          <Table
            columns={[
              { title: "SKU", dataIndex: "sku", key: "sku", width: 100 },
              {
                title: t("StoreAnalysis-Article", "ARTÍCULO"),
                key: "articulo",
                render: (_: unknown, r: (typeof quiebres)[0]) => (
                  <div>
                    <Text>{r.articulo}</Text>
                    {r.externalId && (
                      <div>
                        <Text type="secondary" className={styles.externalId}>
                          {r.externalId}
                        </Text>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                title: t("StoreAnalysis-DaysWithoutStock", "DÍAS SIN STOCK"),
                dataIndex: "diasSinStock",
                key: "diasSinStock",
                align: "right" as const,
                render: (v: number) => (
                  <Text type={v >= 5 ? "danger" : undefined} strong>
                    {v}
                  </Text>
                ),
              },
              {
                title: t("StoreAnalysis-GlobalStock", "STOCK GLOBAL"),
                dataIndex: "stockGlobal",
                key: "stockGlobal",
                align: "right" as const,
                render: (v: number) => fmtN(v),
              },
            ]}
            dataSource={quiebres.map((q, i) => ({ ...q, key: i }))}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            className={styles.dataTable}
            locale={{
              emptyText: (
                <Empty
                  description={t("StoreAnalysis-NoStockouts", "Sin quiebres")}
                />
              ),
            }}
          />
        </Card>

        <Card
          className={`${styles.sectionCard} ${styles.clientsSectionCard}`}
          title={
            <Space>
              <TeamOutlined />
              <span>{t("StoreAnalysis-StoreClients", "Clientes de la tienda")}</span>
            </Space>
          }
        >
          <div className={styles.clientKpiRow}>
            {[
              {
                label: t("StoreAnalysis-UniqueClients", "CLIENTES ÚNICOS"),
                value: fmtN(clientKpis?.clientesUnicos ?? 0),
              },
              {
                label: t("StoreAnalysis-RecurringClients", "CLIENTES RECURRENTES"),
                value: fmtN(clientKpis?.clientesRecurrentes ?? 0),
              },
              {
                label: t("StoreAnalysis-ExclusiveClients", "CLIENTES EXCLUSIVOS"),
                value: fmtN(clientKpis?.clientesExclusivos ?? 0),
              },
              {
                label: t("StoreAnalysis-PurchaseFrequency", "FRECUENCIA COMPRA"),
                value: (clientKpis?.frecuenciaCompra ?? 0).toFixed(1),
              },
            ].map((item, i) => (
              <div className={styles.clientKpiItem} key={i}>
                <Text className={styles.clientKpiLabel}>{item.label}</Text>
                <div className={styles.clientKpiValue}>{item.value}</div>
              </div>
            ))}
          </div>

          <Row gutter={[16, 16]} className={styles.clientTablesRow}>
            <Col xs={24} lg={12}>
              <div className={styles.clientSubPanel}>
                <div className={styles.clientSubPanelTitle}>
                  {t("StoreAnalysis-NewClientsByMonth", "Clientes nuevos por mes")}
                </div>
                <Table
                  columns={[
                    {
                      title: t("StoreAnalysis-Month", "MES"),
                      dataIndex: "mes",
                      key: "mes",
                    },
                    {
                      title: t("StoreAnalysis-Quantity", "CANTIDAD"),
                      dataIndex: "cantidad",
                      key: "cantidad",
                      render: (v: number) => fmtN(v),
                      align: "right" as const,
                    },
                  ]}
                  dataSource={clientesNuevosMes.map((r, i) => ({
                    ...r,
                    key: i,
                  }))}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  size="small"
                  className={styles.dataTable}
                  locale={{
                    emptyText: (
                      <Empty
                        description={t("StoreAnalysis-NoData", "No hay datos")}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ),
                  }}
                />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className={styles.clientSubPanel}>
                <div className={styles.clientSubPanelTitle}>
                  {t("StoreAnalysis-GeoLocation", "Ubicación geográfica")}
                </div>
                <Table
                  columns={[
                    {
                      title: t("StoreAnalysis-Location", "UBICACIÓN"),
                      dataIndex: "ubicacion",
                      key: "ubicacion",
                    },
                    {
                      title: t("StoreAnalysis-Clients", "CLIENTES"),
                      dataIndex: "clientes",
                      key: "clientes",
                      render: (v: number) => fmtN(v),
                      align: "right" as const,
                    },
                    {
                      title: t("StoreAnalysis-Growth", "CRECIMIENTO"),
                      dataIndex: "crecimiento",
                      key: "crecimiento",
                      render: (v: number) => <Vr value={v} />,
                      align: "right" as const,
                    },
                  ]}
                  dataSource={ubicacionGeografica.map((r, i) => ({
                    ...r,
                    key: i,
                  }))}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  size="small"
                  className={styles.dataTable}
                />
              </div>
            </Col>
          </Row>
        </Card>
        </>
      )}
    </div>
  );
};

export default StoreDetail;
